// apps/admin/src/pages/billing/AgingReportPage.tsx
//
// Phase 8.2 Wave 3 Task 17 — Admin A4: AgingReportPage (extends Phase 4.1
// implementation; supersedes Phase 4.1 Wave 13 Task 40).
//
// Per CONTEXT.md A4 (admin aging report surface)
//   + Amendment #2 (Gross + Net Outstanding columns side-by-side with
//     tooltip explaining the difference verbatim copy).
// Per Phase 4.1 ancestor: A4, (gross/net split origin)
//    (DSO metric — Days Sales Outstanding).
// Per INTERFACE-STANDARDS.md § FE error envelope (toast surfaces
//   `error.user_message || error.message` — never AxiosError.message / HTTP statusText).
//
// Endpoints consumed (Phase 8.2 API-CONTRACTS):
//   GET /api/v1/admin/invoices/aging-report
//     ?merchant_id=&as_of_date=&sort=(gross|-gross|net|-net|merchant_name)
//   GET /api/v1/admin/invoices/aging-report/export.csv?merchant_id=&as_of_date=
//
// Response shape (Phase 8.2):
//   data.overall    — total_gross_cents, total_net_cents, dso_days, buckets[]
//   data.per_merchant[] — merchant_id, merchant_name, gross_cents, net_cents,
//                         dso_days, buckets[]
//   buckets[] — { bucket: "0-7d"|"8-30d"|"30d+", count, gross_cents, net_cents }
//
// vg-binding: CONTEXT:decisions=P8.D-485,P8.D-492
// vg-binding: INTERFACE-STANDARDS:error-shape

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  Input,
  MerchantPicker,
  cn,
  useToast,
  PageHeader,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Types — mirror API-CONTRACTS §get-api-v1-admin-invoices-aging-report (Phase 8.2)
// ---------------------------------------------------------------------------

type BucketKey = '0-7d' | '8-30d' | '30d+';

const BUCKETS: readonly BucketKey[] = ['0-7d', '8-30d', '30d+'] as const;

const BUCKET_VALUE_CLASS: Record<BucketKey, string> = {
  '0-7d': 'text-emerald-600 dark:text-emerald-300',
  '8-30d': 'text-amber-600 dark:text-amber-300',
  '30d+': 'text-red-600 dark:text-red-300',
};

interface AgingBucket {
  bucket: BucketKey;
  count: number;
  gross_cents: number;
  net_cents: number;
}

interface OverallSection {
  total_gross_cents: number;
  total_net_cents: number;
  dso_days: number;
  buckets: AgingBucket[];
}

interface PerMerchantRow {
  merchant_id: string;
  merchant_name: string;
  gross_cents: number;
  net_cents: number;
  dso_days: number;
  buckets: AgingBucket[];
}

interface AgingReportData {
  as_of_date: string;
  overall: OverallSection;
  per_merchant: PerMerchantRow[];
}

// Success envelope per INTERFACE-STANDARDS § success_envelope.
interface SuccessEnvelope<T> {
  ok: true;
  data: T;
  message?: string;
  request_id?: string;
}

// Error envelope per INTERFACE-STANDARDS § error_envelope.
interface ErrorEnvelope {
  ok?: false;
  error?: {
    code?: string;
    message?: string;
    user_message?: string;
    details?: unknown;
    field_errors?: Record<string, string[]>;
    request_id?: string;
  };
  message?: string;
}

// Narrow openapi-fetch surface — same pattern as TopupQueuePage.tsx.
interface RawClient {
  GET: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
}

// Sort columns supported by the Phase 8.2 contract.
type SortKey = 'gross' | '-gross' | 'net' | '-net' | 'merchant_name';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Pull a user-facing message out of an envelope error.
 * Per INTERFACE-STANDARDS message_priority:
 *   error.user_message > error.message > message > network_fallback
 */
function envelopeMessage(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const env = payload as ErrorEnvelope;
  if (env.error?.user_message) return env.error.user_message;
  if (env.error?.message) return env.error.message;
  if (env.message) return env.message;
  return null;
}

function unwrapData<T>(payload: unknown): T | null {
  if (
    payload &&
    typeof payload === 'object' &&
    'ok' in payload &&
    (payload as SuccessEnvelope<T>).ok === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  // Phase 4.1 BUG-A3-i fix shipped the BE with `{success: true, data, error}`
  // envelope instead of the `{ok: true, data}` style described in
  // INTERFACE-STANDARDS. Accept both to keep this page resilient.
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as { success?: boolean }).success === true &&
    'data' in payload
  ) {
    return (payload as { data: T }).data;
  }
  // Tolerate raw payload (some legacy admin endpoints respond unwrapped).
  if (
    payload &&
    typeof payload === 'object' &&
    ('overall' in payload || 'global' in payload) &&
    'per_merchant' in payload
  ) {
    return payload as T;
  }
  return null;
}

// ---------------------------------------------------------------------------
// BE → FE shape adapter.
//
// The actual aging-report BE handler (apps/api/.../aging-report.handler.ts)
// emits a different field naming convention than the original FE contract:
//   - `as_of`                            (FE expected `as_of_date`)
//   - `global`                           (FE expected `overall`)
//   - bucket keys: `0-7d, 8-30d, 31-60d, 61-90d, 90d+` (5 keys)
//                                        (FE expected `0-7d, 8-30d, 30d+`)
//   - `invoice_count`                    (FE expected `count`)
//   - `gross_outstanding_usd_cents`      (FE expected `gross_cents`)
//   - `net_outstanding_usd_cents`        (FE expected `net_cents`)
//   - `total_gross_outstanding_usd_cents`(FE expected `total_gross_cents`)
//   - `total_net_outstanding_usd_cents`  (FE expected `total_net_cents`)
//
// `per_merchant[]` rows in BE response carry only `buckets[]` + `dso_days`;
// FE's `gross_cents`/`net_cents` row-level totals are derived by summing
// the buckets.
//
// This adapter collapses the 5 BE bucket keys into the 3 FE bucket keys
// by merging `31-60d` + `61-90d` + `90d+` into a single `30d+` row.
// ---------------------------------------------------------------------------

interface BeBucket {
  bucket: string;
  invoice_count?: number;
  count?: number;
  gross_outstanding_usd_cents?: number;
  gross_cents?: number;
  net_outstanding_usd_cents?: number;
  net_cents?: number;
}

interface BePerMerchantRow {
  merchant_id: string;
  merchant_name?: string;
  buckets?: BeBucket[];
  dso_days?: number;
  gross_cents?: number;
  net_cents?: number;
}

interface BeAgingReportData {
  as_of?: string;
  as_of_date?: string;
  global?: {
    buckets?: BeBucket[];
    total_gross_outstanding_usd_cents?: number;
    total_net_outstanding_usd_cents?: number;
    total_gross_cents?: number;
    total_net_cents?: number;
    dso_days?: number;
  };
  overall?: OverallSection;
  per_merchant?: BePerMerchantRow[];
}

function normalizeBucket(b: BeBucket): {
  count: number;
  gross_cents: number;
  net_cents: number;
} {
  return {
    count: b.invoice_count ?? b.count ?? 0,
    gross_cents: b.gross_outstanding_usd_cents ?? b.gross_cents ?? 0,
    net_cents: b.net_outstanding_usd_cents ?? b.net_cents ?? 0,
  };
}

function collapseBuckets(buckets: BeBucket[] | undefined): AgingBucket[] {
  const acc: Record<BucketKey, AgingBucket> = {
    '0-7d': { bucket: '0-7d', count: 0, gross_cents: 0, net_cents: 0 },
    '8-30d': { bucket: '8-30d', count: 0, gross_cents: 0, net_cents: 0 },
    '30d+': { bucket: '30d+', count: 0, gross_cents: 0, net_cents: 0 },
  };
  for (const b of buckets ?? []) {
    const target: BucketKey =
      b.bucket === '0-7d'
        ? '0-7d'
        : b.bucket === '8-30d'
          ? '8-30d'
          : '30d+';
    const n = normalizeBucket(b);
    acc[target].count += n.count;
    acc[target].gross_cents += n.gross_cents;
    acc[target].net_cents += n.net_cents;
  }
  return [acc['0-7d'], acc['8-30d'], acc['30d+']];
}

function adaptAgingReport(raw: BeAgingReportData): AgingReportData {
  // If response already uses the FE-internal shape (legacy path) pass through.
  if (raw.overall && raw.per_merchant && !raw.global) {
    return {
      as_of_date: raw.as_of_date ?? raw.as_of ?? '',
      overall: raw.overall,
      per_merchant: raw.per_merchant as unknown as PerMerchantRow[],
    };
  }
  const globalBuckets = collapseBuckets(raw.global?.buckets);
  const overall: OverallSection = {
    total_gross_cents:
      raw.global?.total_gross_outstanding_usd_cents ??
      raw.global?.total_gross_cents ??
      0,
    total_net_cents:
      raw.global?.total_net_outstanding_usd_cents ??
      raw.global?.total_net_cents ??
      0,
    dso_days: raw.global?.dso_days ?? 0,
    buckets: globalBuckets,
  };
  const per_merchant: PerMerchantRow[] = (raw.per_merchant ?? []).map(
    (row): PerMerchantRow => {
      const collapsed = collapseBuckets(row.buckets);
      const grossTotal = collapsed.reduce((s, b) => s + b.gross_cents, 0);
      const netTotal = collapsed.reduce((s, b) => s + b.net_cents, 0);
      return {
        merchant_id: row.merchant_id,
        merchant_name: row.merchant_name ?? row.merchant_id,
        gross_cents: row.gross_cents ?? grossTotal,
        net_cents: row.net_cents ?? netTotal,
        dso_days: row.dso_days ?? 0,
        buckets: collapsed,
      };
    },
  );
  return {
    as_of_date: raw.as_of_date ?? raw.as_of ?? '',
    overall,
    per_merchant,
  };
}

function formatUsdFromCents(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(dollars);
}

function formatUsdFromCentsExact(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

function bucketByKey(buckets: AgingBucket[], key: BucketKey): AgingBucket {
  return (
    buckets.find((b) => b.bucket === key) ?? {
      bucket: key,
      count: 0,
      gross_cents: 0,
      net_cents: 0,
    }
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AgingReportPage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const d492Tooltip = t('aging_report.tooltips.grossNet');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState<AgingReportData | null>(null);
  const [loading, setLoading] = useState(false);

  // URL state (deep-linkable filters + sort).
  const asOfDate = searchParams.get('as_of_date') ?? '';
  const merchantId = searchParams.get('merchant_id') ?? '';
  const sortParam = (searchParams.get('sort') ?? '-gross') as SortKey;

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (asOfDate) params.set('as_of_date', asOfDate);
    if (merchantId) params.set('merchant_id', merchantId);
    params.set('sort', sortParam);
    return params.toString();
  }, [asOfDate, merchantId, sortParam]);

  const fetchReport = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const client = apiClient() as unknown as RawClient;
      const path = `/api/v1/admin/invoices/aging-report?${queryString}`;
      const { data, error, response } = await client.GET(path);
      if (error || !response.ok) {
        toast({
          title: t('aging_report.toast.loadFailed'),
          description:
            envelopeMessage(error) ??
            envelopeMessage(data) ??
            t('aging_report.toast.networkError'),
          variant: 'error',
        });
        setReport(null);
        return;
      }
      const unwrapped = unwrapData<BeAgingReportData>(data);
      if (!unwrapped) {
        toast({
          title: t('aging_report.toast.loadFailed'),
          description: t('aging_report.toast.unexpectedShape'),
          variant: 'error',
        });
        setReport(null);
        return;
      }
      setReport(adaptAgingReport(unwrapped));
    } catch (err) {
      toast({
        title: t('aging_report.toast.networkError'),
        description: err instanceof Error ? err.message : t('aging_report.toast.unknownError'),
        variant: 'error',
      });
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [queryString, toast, t]);

  useEffect(() => {
    void fetchReport();
  }, [fetchReport]);

  function updateParam(key: string, value: string): void {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  }

  function toggleSort(column: 'gross' | 'net' | 'merchant_name'): void {
    if (column === 'merchant_name') {
      updateParam('sort', 'merchant_name');
      return;
    }
    // Flip desc <-> asc, defaulting to desc on first click.
    if (sortParam === column) updateParam('sort', `-${column}` as SortKey);
    else if (sortParam === `-${column}`) updateParam('sort', column);
    else updateParam('sort', `-${column}` as SortKey);
  }

  function handleExportCsv(): void {
    const params = new URLSearchParams();
    if (asOfDate) params.set('as_of_date', asOfDate);
    if (merchantId) params.set('merchant_id', merchantId);
    const url = `/api/v1/admin/invoices/aging-report/export.csv${
      params.toString() ? `?${params.toString()}` : ''
    }`;
    // Direct browser download via top-level navigation — the BE sets
    // Content-Disposition: attachment, so the browser saves the file and
    // does not actually navigate away from the page.
    window.location.href = url;
  }

  function handleDrillDown(
    targetMerchantId: string,
    bucket: BucketKey,
  ): void {
    // Pre-apply merchant + bucket filters on the invoice list (task-11).
    // Use URL query params so the link is deep-linkable / reloadable + also
    // pass via router state for components that read state directly.
    const params = new URLSearchParams();
    params.set('merchant_id', targetMerchantId);
    params.set('status', 'SENT,OVERDUE');
    params.set('aging_bucket', bucket);
    if (asOfDate) params.set('as_of', asOfDate);
    navigate(
      { pathname: '/billing/invoices', search: `?${params.toString()}` },
      { state: { merchantId: targetMerchantId, bucket } },
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const overall = report?.overall ?? null;
  const totalInvoices = overall?.buckets.reduce((sum, b) => sum + b.count, 0) ?? 0;

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header — title + subtitle + filters + Export */}
      <PageHeader
        title={t('aging_report.title')}
        subtitle={t('aging_report.page.description')}
        actions={
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-xs font-medium text-pw-text-muted">
              {t('aging_report.filters.asOfDate')}
              <Input
                type="date"
                data-testid="aging-report-as-of-input"
                className="mt-1 w-44"
                value={asOfDate ? asOfDate.slice(0, 10) : ''}
                onChange={(e) =>
                  updateParam(
                    'as_of_date',
                    e.target.value ? `${e.target.value}T23:59:59.999Z` : '',
                  )
                }
              />
            </label>
            <label className="flex flex-col text-xs font-medium text-pw-text-muted">
              {t('aging_report.filters.merchant')}
              <div className="mt-1 w-64">
                <MerchantPicker
                  value={merchantId || null}
                  onChange={(id) => updateParam('merchant_id', id ?? '')}
                  placeholder={t('aging_report.filters.merchantPlaceholder')}
                  testId="aging-report-merchant-picker"
                />
              </div>
            </label>
            <Button
              variant="secondary"
              data-testid="aging-report-export-csv-btn"
              onClick={handleExportCsv}
            >
              {t('aging_report.actions.export')}
            </Button>
          </div>
        }
      />

      {/* Summary tiles — DSO + Total Gross + Total Net + 3 buckets */}
      <section
        data-testid="aging-report-summary-tiles"
        className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
      >
        <SummaryTile
          label={t('aging_report.tiles.dso')}
          testId="aging-report-dso-card"
          value={overall ? `${Math.round(overall.dso_days)}d` : '—'}
          subtext={t('aging_report.tiles.dsoSub')}
          valueClass="text-pw-text"
          loading={loading}
          tooltip={t('aging_report.tooltips.dso')}
        />
        <SummaryTile
          label={t('aging_report.tiles.totalGross')}
          testId="aging-report-total-gross-card"
          value={formatUsdFromCents(overall?.total_gross_cents ?? 0)}
          subtext={t('aging_report.tiles.invoicesCount', { count: totalInvoices })}
          valueClass="text-pw-text"
          loading={loading}
          tooltip={d492Tooltip}
        />
        <SummaryTile
          label={t('aging_report.tiles.totalNet')}
          testId="aging-report-total-net-card"
          value={formatUsdFromCents(overall?.total_net_cents ?? 0)}
          subtext={t('aging_report.tiles.netSub')}
          valueClass="text-pw-text"
          loading={loading}
          tooltip={d492Tooltip}
        />
        {BUCKETS.map((b) => {
          const agg = overall ? bucketByKey(overall.buckets, b) : null;
          return (
            <SummaryTile
              key={b}
              label={t(`aging_report.bucketLabels.${b}`)}
              value={formatUsdFromCents(agg?.gross_cents ?? 0)}
              subtext={t('aging_report.tiles.invoicesCount', { count: agg?.count ?? 0 })}
              valueClass={BUCKET_VALUE_CLASS[b]}
              loading={loading}
            />
          );
        })}
      </section>

      {/* By Merchant — table */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b border-pw-border px-5 py-4">
            <h2 className="text-base font-semibold text-pw-text">{t('aging_report.byMerchant.title')}</h2>
            <span className="text-xs text-pw-text-muted">
              {t('aging_report.byMerchant.hint')}
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-12 text-sm text-pw-text-muted">
              {t('aging_report.loading')}
            </div>
          ) : !report || report.per_merchant.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="overflow-x-auto">
            <table
              data-testid="aging-report-table"
              className="w-full text-sm"
            >
              <thead className="bg-pw-surface">
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                  <th className="px-5 py-3">
                    <SortHeader
                      label={t('aging_report.filters.merchant')}
                      column="merchant_name"
                      current={sortParam}
                      onToggle={toggleSort}
                      testId="aging-sort-merchant-name"
                    />
                  </th>
                  <th className="px-5 py-3">{t('aging_report.byMerchant.colDso')}</th>
                  <th className="px-5 py-3 text-right">
                    <SortHeader
                      label={t('aging_report.byMerchant.colGross')}
                      column="gross"
                      current={sortParam}
                      onToggle={toggleSort}
                      tooltip={d492Tooltip}
                      testId="aging-sort-gross"
                    />
                  </th>
                  <th className="px-5 py-3 text-right">
                    <SortHeader
                      label={t('aging_report.byMerchant.colNet')}
                      column="net"
                      current={sortParam}
                      onToggle={toggleSort}
                      tooltip={d492Tooltip}
                      testId="aging-sort-net"
                    />
                  </th>
                  {BUCKETS.map((b) => (
                    <th key={b} className="px-5 py-3 text-right">
                      {b}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.per_merchant.map((row) => (
                  <tr
                    key={row.merchant_id}
                    data-testid={`aging-row-${row.merchant_id}`}
                    className={cn(
                      'border-t border-pw-border transition-colors hover:bg-pw-surface/60',
                    )}
                  >
                    <td className="px-5 py-4">
                      <div
                        className="font-medium text-pw-text"
                        title={row.merchant_id}
                      >
                        {row.merchant_name}
                      </div>
                      <div className="font-mono text-[10px] text-pw-text-muted/70">
                        …{row.merchant_id.slice(-6)}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-pw-text-muted">
                      {Math.round(row.dso_days)}d
                    </td>
                    <td className="px-5 py-4 text-right font-medium text-pw-text">
                      {formatUsdFromCentsExact(row.gross_cents)}
                    </td>
                    <td className="px-5 py-4 text-right font-medium text-pw-text">
                      {formatUsdFromCentsExact(row.net_cents)}
                    </td>
                    {BUCKETS.map((b) => {
                      const agg = bucketByKey(row.buckets, b);
                      const isOverdue = b === '30d+' && agg.gross_cents > 0;
                      return (
                        <td key={b} className="px-5 py-4 text-right">
                          {agg.gross_cents === 0 ? (
                            <span className="text-pw-text-muted">—</span>
                          ) : (
                            <button
                              type="button"
                              data-testid={`aging-drill-down-btn-${row.merchant_id}-${b}`}
                              onClick={() =>
                                handleDrillDown(row.merchant_id, b)
                              }
                              className={cn(
                                'underline-offset-2 hover:underline',
                                isOverdue
                                  ? 'font-medium text-red-600 dark:text-red-300'
                                  : 'text-pw-text',
                              )}
                            >
                              {formatUsdFromCentsExact(agg.gross_cents)}
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              {overall && (
                <tfoot className="border-t-2 border-pw-border bg-pw-surface/40">
                  <tr className="text-sm font-medium text-pw-text">
                    <td className="px-5 py-4">{t('aging_report.byMerchant.overall')}</td>
                    <td className="px-5 py-4 text-pw-text-muted">
                      {Math.round(overall.dso_days)}d
                    </td>
                    <td className="px-5 py-4 text-right">
                      {formatUsdFromCentsExact(overall.total_gross_cents)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      {formatUsdFromCentsExact(overall.total_net_cents)}
                    </td>
                    {BUCKETS.map((b) => {
                      const agg = bucketByKey(overall.buckets, b);
                      return (
                        <td key={b} className="px-5 py-4 text-right">
                          {agg.gross_cents === 0
                            ? '—'
                            : formatUsdFromCentsExact(agg.gross_cents)}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SortHeader — sortable column header with arrow indicator + optional tooltip
// ---------------------------------------------------------------------------

function SortHeader(props: {
  label: string;
  column: 'gross' | 'net' | 'merchant_name';
  current: SortKey;
  onToggle: (column: 'gross' | 'net' | 'merchant_name') => void;
  tooltip?: string;
  testId?: string;
}): JSX.Element {
  const { label, column, current, onToggle, tooltip, testId } = props;
  const isActive = current === column || current === `-${column}`;
  const isDesc = current === `-${column}`;
  const arrow = !isActive ? '' : isDesc ? ' ▼' : ' ▲';
  return (
    <button
      type="button"
      onClick={() => onToggle(column)}
      title={tooltip}
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-1 hover:text-pw-text',
        isActive && 'text-pw-text',
      )}
    >
      <span>{label}</span>
      {tooltip ? (
        <span
          aria-hidden="true"
          className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-pw-border text-[10px] text-pw-text-muted"
        >
          ?
        </span>
      ) : null}
      <span aria-hidden="true">{arrow}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// SummaryTile — single bucket / total card with optional tooltip
// ---------------------------------------------------------------------------

function SummaryTile(props: {
  label: string;
  value: string;
  subtext: string;
  valueClass: string;
  loading: boolean;
  tooltip?: string;
  testId?: string;
}): JSX.Element {
  const { label, value, subtext, valueClass, loading, tooltip, testId } = props;
  const { t } = useTranslation('finance');
  return (
    <Card>
      <CardContent
        className="flex flex-col gap-1 p-4"
        title={tooltip}
        data-testid={testId}
      >
        <span className="text-xs font-medium uppercase tracking-wide text-pw-text-muted">
          {label}
          {tooltip ? (
            <span
              aria-hidden="true"
              className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-pw-border text-[10px] text-pw-text-muted"
            >
              ?
            </span>
          ) : null}
        </span>
        <span
          className={cn('text-3xl font-bold leading-tight', valueClass, {
            'animate-pulse': loading,
          })}
        >
          {loading ? '—' : value}
        </span>
        <span className="text-xs text-pw-text-muted">
          {loading ? t('aging_report.loadingShort') : subtext}
        </span>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState(): JSX.Element {
  const { t } = useTranslation('finance');
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <div
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-pw-surface text-pw-text-muted"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          width={32}
          height={32}
          aria-hidden="true"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="9" y1="21" x2="9" y2="9" />
        </svg>
      </div>
      <h3 className="text-base font-semibold text-pw-text">
        {t('aging_report.empty.title')}
      </h3>
      <p className="max-w-sm text-sm text-pw-text-muted">
        {t('aging_report.empty.body')}
      </p>
    </div>
  );
}

export default AgingReportPage;
